import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  FaCalendarAlt,
  FaCarSide,
  FaCheckCircle,
  FaClipboardList,
  FaFilter,
  FaIdCard,
  FaImage,
  FaRedo,
  FaRoad,
  FaShieldAlt,
  FaTimesCircle,
  FaUserCheck,
  FaUserClock,
  FaUserPlus,
} from "react-icons/fa";
import DriversMetricsService from "../services/DriversMetricsService";

const initialMetrics = {
  formAttempted: 0,
  allDocsSubmitted: 0,
  singleDocNotSubmitted: 0,
  dlAndAadhaarSubmitted: 0,
  onlyDlSubmitted: 0,
  profilePicUploaded: 0,
  profilePicNotUploaded: 0,
  drivingChoicesProvided: 0,
  drivingChoicesNotProvided: 0,
  dlExpired: 0,
  pending: 0,
  inRevisit: 0,
  verified: 0,
  rejected: 0,
  onboarded: 0,
};

const DashboardDriversMetrics = () => {
  const [metrics, setMetrics] = useState(initialMetrics);
  const [loading, setLoading] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
  });

  const fetchMetrics = async (params = {}) => {
    setLoading(true);
    try {
      const response = await DriversMetricsService.getDriversMetrics(params);
      if (response?.success) {
        setMetrics(response?.data?.metrics || initialMetrics);
        setFiltersApplied(Boolean(response?.data?.filtersApplied));
      } else {
        toast.error("Failed to load drivers metrics");
      }
    } catch (error) {
      console.error("Drivers metrics fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  const handleApply = () => {
    const { startDate, endDate } = filters;
    const hasAnyDate = Boolean(startDate || endDate);
    const hasBothDates = Boolean(startDate && endDate);

    if (hasAnyDate && !hasBothDates) {
      toast.warning("Please select both start and end dates");
      return;
    }

    if (hasBothDates && startDate > endDate) {
      toast.warning("Start date cannot be greater than end date");
      return;
    }

    const params = {};
    if (hasBothDates) {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    fetchMetrics(params);
  };

  const handleClear = () => {
    setFilters({ startDate: "", endDate: "" });
    fetchMetrics();
  };

  const cards = [
    { key: "formAttempted", label: "Form Attempted", sublabel: "Includes OTP + Bulk/Quick/Manual add", value: metrics.formAttempted, icon: <FaClipboardList className="text-2xl" />, style: "from-slate-500 to-slate-600" },
    { key: "allDocsSubmitted", label: "All Docs Submitted", value: metrics.allDocsSubmitted, icon: <FaIdCard className="text-2xl" />, style: "from-blue-500 to-blue-600" },
    { key: "singleDocNotSubmitted", label: "Single Doc Not Submitted", value: metrics.singleDocNotSubmitted, icon: <FaShieldAlt className="text-2xl" />, style: "from-indigo-500 to-indigo-600" },
    { key: "dlAndAadhaarSubmitted", label: "DL & Aadhaar Submitted", value: metrics.dlAndAadhaarSubmitted, icon: <FaCheckCircle className="text-2xl" />, style: "from-cyan-500 to-cyan-600" },
    { key: "onlyDlSubmitted", label: "Only DL Submitted", value: metrics.onlyDlSubmitted, icon: <FaRoad className="text-2xl" />, style: "from-teal-500 to-teal-600" },
    { key: "profilePicUploaded", label: "Profile Pic Uploaded", value: metrics.profilePicUploaded, icon: <FaImage className="text-2xl" />, style: "from-pink-500 to-pink-600" },
    { key: "profilePicNotUploaded", label: "Profile Pic Not Uploaded", value: metrics.profilePicNotUploaded, icon: <FaImage className="text-2xl" />, style: "from-gray-500 to-gray-600" },
    { key: "drivingChoicesProvided", label: "Driving Choices Provided", value: metrics.drivingChoicesProvided, icon: <FaCarSide className="text-2xl" />, style: "from-green-500 to-green-600" },
    { key: "drivingChoicesNotProvided", label: "Driving Choices Not Provided", value: metrics.drivingChoicesNotProvided, icon: <FaCarSide className="text-2xl" />, style: "from-lime-600 to-lime-700" },
    { key: "dlExpired", label: "DL Expired", sublabel: "Date of Expiry", value: metrics.dlExpired, icon: <FaTimesCircle className="text-2xl" />, style: "from-red-500 to-red-600" },
    { key: "pending", label: "Pending", value: metrics.pending, icon: <FaUserClock className="text-2xl" />, style: "from-amber-500 to-amber-600" },
    { key: "inRevisit", label: "In Revisit", value: metrics.inRevisit, icon: <FaUserCheck className="text-2xl" />, style: "from-orange-500 to-orange-600" },
    { key: "verified", label: "Verified", value: metrics.verified, icon: <FaUserCheck className="text-2xl" />, style: "from-emerald-500 to-emerald-600" },
    { key: "rejected", label: "Rejected", value: metrics.rejected, icon: <FaTimesCircle className="text-2xl" />, style: "from-rose-500 to-rose-600" },
    { key: "onboarded", label: "Onboarded", value: metrics.onboarded, icon: <FaUserPlus className="text-2xl" />, style: "from-purple-500 to-purple-600" },
  ];

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Drivers Metrics</h3>
        <p className="text-gray-600 text-sm">
          {filtersApplied
            ? "Filtered by driver createdAt date range"
            : "Overall totals across all drivers"}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, startDate: e.target.value }))
            }
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, endDate: e.target.value }))
            }
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={handleApply}
            disabled={loading}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-60"
          >
            <FaFilter className="inline mr-2" />
            Filter Apply
          </button>
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={handleClear}
            disabled={loading}
            className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 disabled:opacity-60"
          >
            <FaRedo className="inline mr-2" />
            Clear
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-600">
          <FaCalendarAlt className="inline mr-2 animate-pulse" />
          Loading drivers metrics...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((card) => (
            <div
              key={card.key}
              className={`bg-gradient-to-br ${card.style} rounded-xl shadow-lg p-6 text-white`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white/90">{card.label}</p>
                  {card.sublabel ? (
                    <p className="text-xs text-white/75 mt-0.5">{card.sublabel}</p>
                  ) : null}
                  <p className="text-3xl font-bold mt-1">
                    {Number(card.value || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-white/20 rounded-full p-3">{card.icon}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardDriversMetrics;
